import React, { useEffect, useMemo, useState } from "react";
import { BsArrowLeft } from "react-icons/bs";
import { toast } from "react-hot-toast";
import { useLocation, useNavigate } from "react-router-dom";
import { KYC_LIMIT_LABELS } from "../../../constants/kycLimits";
import LimitsInfoModal from "./LimitsInfoModal";
import Level1Flow from "./Level1Flow";
import Level2Flow from "./Level2Flow";
import Level3Flow from "./Level3Flow";
import useKycDetails from "../../../hooks/useKycDetails";
import useLimits from "../../../hooks/useLimits";

const isStagePending = (
  stage: { status?: string } | null | undefined
): boolean => {
  if (!stage) return false;
  return stage.status?.toLowerCase() === "pending";
};

const isStageComplete = (
  stage: { status?: string; data?: { status?: string } } | null | undefined
): boolean => {
  if (!stage) return false;
  const s = stage.status?.toLowerCase();
  const dataStatus = stage.data?.status?.toLowerCase();
  return s === "success" || s === "approved" || dataStatus === "approved";
};

const KYC_VERIFICATION_PATH = "/kyc-verification";

const KycOnboarding = (
  props: {
    setOpenKycOnboarding?: (open: boolean) => void;
    setOpenKyc?: (open: boolean) => void;
    setOpenLevel1Flow?: (open: boolean) => void;
    setOpenLevel2Flow?: (open: boolean) => void;
    setOpenLevel3Flow?: (open: boolean) => void;
    setOpenBankDetails?: (open: boolean) => void;
    setOpenAddPhone?: (open: boolean) => void;
  } = {}
) => {
  const {
    setOpenKycOnboarding: setOpenKycOnboardingProp,
    setOpenLevel1Flow: setOpenLevel1FlowProp,
    setOpenLevel2Flow: setOpenLevel2FlowProp,
    setOpenLevel3Flow: setOpenLevel3FlowProp,
  } = props;

  const navigate = useNavigate();
  const location = useLocation();
  const isPage = !setOpenKycOnboardingProp;

  const [openLevel1Flow, setOpenLevel1Flow] = useState(false);
  const [openLevel2Flow, setOpenLevel2Flow] = useState(false);
  const [openLevel3Flow, setOpenLevel3Flow] = useState(false);

  const { data: kycDetails } = useKycDetails();
  const { limits } = useLimits();
  const [showLimitsModal, setShowLimitsModal] = useState(false);
  const details = kycDetails?.kycDetails;
  const level1Complete = useMemo(
    () => isStageComplete(details?.first_stage),
    [details?.first_stage]
  );
  const level2Complete = useMemo(
    () => isStageComplete(details?.second_stage),
    [details?.second_stage]
  );
  const level3Complete = useMemo(
    () => isStageComplete(details?.third_stage),
    [details?.third_stage]
  );
  const allComplete = level1Complete && level2Complete && level3Complete;

  const currentLevel = useMemo(() => {
    if (level3Complete) return 3;
    if (level2Complete) return 2;
    if (level1Complete) return 1;

    return 0;
  }, [level1Complete, level2Complete, level3Complete]);

  const hasPending =
    isStagePending(details?.first_stage) ||
    isStagePending(details?.second_stage) ||
    isStagePending(details?.third_stage);
  const pendingLevel = details
    ? isStagePending(details.first_stage)
      ? 1
      : isStagePending(details.second_stage)
      ? 2
      : isStagePending(details.third_stage)
      ? 3
      : null
    : null;

  const currentTradingLimit =
    currentLevel > 0 ? limits[currentLevel - 1].trading : "0";
  const currentWithdrawalLimit =
    currentLevel > 0 ? limits[currentLevel - 1].withdrawal : "0";

  const handleUpgrade = () => {
    if (hasPending && pendingLevel) {
      toast(
        `Your Level ${pendingLevel} verification is pending. You will be notified when it's approved.`,
        {
          style: {
            background: "#fef08a",
            color: "#713f12",
          },
        }
      );
      return;
    }
    if (isPage) {
      if (currentLevel === 0) setOpenLevel1Flow(true);
      else if (currentLevel === 1) setOpenLevel2Flow(true);
      else if (currentLevel === 2) setOpenLevel3Flow(true);
    } else {
      setOpenKycOnboardingProp?.(false);
      if (currentLevel === 0) setOpenLevel1FlowProp?.(true);
      else if (currentLevel === 1) setOpenLevel2FlowProp?.(true);
      else if (currentLevel === 2) setOpenLevel3FlowProp?.(true);
    }
  };

  const handleBack = () => {
    if (isPage) navigate("/settings");
    else setOpenKycOnboardingProp?.(false);
  };

  useEffect(() => {
    const state = location.state as { openLevel2Flow?: boolean } | null;
    if (!isPage || !state?.openLevel2Flow) return;

    if (hasPending && pendingLevel) {
      toast(
        `Your Level ${pendingLevel} verification is pending. You will be notified when it's approved.`,
        {
          style: {
            background: "#fef08a",
            color: "#713f12",
          },
        }
      );
    } else if (level1Complete && !level2Complete) {
      setOpenLevel2Flow(true);
    } else if (!level1Complete) {
      setOpenLevel1Flow(true);
    }

    navigate(KYC_VERIFICATION_PATH, { replace: true, state: {} });
  }, [
    isPage,
    location.state,
    hasPending,
    pendingLevel,
    level1Complete,
    level2Complete,
    navigate,
  ]);

  return (
    <div
      className={
        isPage
          ? "min-h-screen font-manrope pt-16 pb-16 bg-[#E7EBEE] dark:bg-primary_dark"
          : "fixed inset-0 flex font-manrope justify-start items-start pt-16 overflow-auto pb-16 bg-[#E7EBEE] dark:bg-primary_dark dark:bg-opacity-95 backdrop-blur-sm"
      }
    >
      <div
        className={`w-11/12 mds:w-8/12 md:7/12 border dark:border-[#303030] bg-white rounded-xl mx-auto p-6 dark:bg-[#192025] lgss:w-2/5 xxl:w-1/3`}
      >
        <div className="w-full flex justify-start gap-6 items-center">
          <button onClick={handleBack}>
            <BsArrowLeft className="text-black dark:text-white text-[20px]" />
          </button>
          <h4 className="text-gray-900 dark:text-gray-100 font-sora font-medium text-[20px]">
            Verify your account
          </h4>
        </div>

        {/* Current limits - two cards */}
        <div className="mt-8 grid grid-cols-2 gap-3">
          <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-600 dark:bg-[#1C2227]">
            <p className="text-gray-500 dark:text-gray-400 text-[11px] font-semibold uppercase tracking-wide">
              TRADING
            </p>
            <p className="text-gray-900 dark:text-gray-100 font-bold text-[22px] mt-1">
              ${currentTradingLimit}
            </p>
            <p className="text-[#BE6B0A] dark:text-[#E8A84A] text-[12px] mt-0.5">
              {KYC_LIMIT_LABELS.tradingSubtitle}
            </p>
          </div>
          <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-600 dark:bg-[#1C2227]">
            <p className="text-gray-500 dark:text-gray-400 text-[11px] font-semibold uppercase tracking-wide">
              WITHDRAWAL
            </p>
            <p className="text-gray-900 dark:text-gray-100 font-bold text-[22px] mt-1">
              ${currentWithdrawalLimit}
            </p>
            <p className="text-[#BE6B0A] dark:text-[#E8A84A] text-[12px] mt-0.5">
              {KYC_LIMIT_LABELS.withdrawalSubtitle}
            </p>
          </div>
        </div>

        {/* Progress */}
        <div className="mt-4 flex items-center gap-2">
          <div className="flex-1 h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
            <div
              className="h-full bg-secondary_button rounded-full transition-all"
              style={{ width: `${(currentLevel / 3) * 100}%` }}
            />
          </div>
          <span className="text-gray-500 dark:text-gray-400 text-[13px] font-medium">
            {currentLevel}/3
          </span>
        </div>

        {/* Level cards */}
        <div className="mt-6 space-y-3">
          {limits.map((item) => {
            const isComplete =
              item.level === 1
                ? level1Complete
                : item.level === 2
                ? level2Complete
                : level3Complete;
            const isPending = item.level === pendingLevel;
            return (
              <div
                key={item.level}
                className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-600 dark:bg-[#1C2227]"
              >
                <div className="flex items-center gap-2 flex-wrap">
                  <h5 className="text-gray-900 dark:text-gray-100 font-semibold text-[15px]">
                    Level {item.level}
                  </h5>
                  {isPending && (
                    <span className="text-[#B45309] dark:text-[#FBBF24] text-[9px] font-bold uppercase">
                      PENDING
                    </span>
                  )}
                  {!isPending && item.level === currentLevel && (
                    <span className="text-[#0D851D] dark:text-green-400 text-[9px] font-bold uppercase">
                      CURRENT LEVEL
                    </span>
                  )}
                  {!isPending && isComplete && item.level !== currentLevel && (
                    <span className="text-[#0D851D] dark:text-green-400 text-[9px] font-bold uppercase">
                      COMPLETED
                    </span>
                  )}
                </div>
                <p className="text-gray-500 dark:text-gray-400 text-[13px] mt-1">
                  ${item.trading} trading • ${item.withdrawal} withdrawal
                </p>
              </div>
            );
          })}
        </div>

        {allComplete ? (
          <>
            {/* Premium Benefits */}
            <div className="mt-6 rounded-xl border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-600 dark:bg-[#1C2227]">
              <h5 className=" font-bold text-[15px] flex items-center text-gray-900 dark:text-gray-100 gap-1.5">
                <span aria-hidden>🌟</span> Premium Benefits
              </h5>
              <p className="text-gray-500 dark:text-gray-400 text-[12px] mt-2">
                You now have access to the highest trading limits, priority
                support, and faster transaction processing.
              </p>
            </div>
            <button
              onClick={() => navigate("/dashboard")}
              className="mt-8 w-full h-[52px] rounded-xl bg-secondary_button text-white font-semibold text-[15px]"
            >
              Start Trading
            </button>
          </>
        ) : (
          <>
            {/* Why verify */}
            <div className="mt-6 rounded-xl border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-600 dark:bg-[#1C2227]">
              <h5 className="text-red-600 dark:text-red-400 font-bold text-[15px]">
                Why verify your account?
              </h5>
              <p className="text-gray-500 dark:text-gray-400 text-[13px] mt-2">
                {currentLevel === 1
                  ? `Upgrade to Level 2 to unlock $${limits[1].trading} trading and $${limits[1].withdrawal} withdrawal limits.`
                  : currentLevel === 2
                  ? `Upgrade to Level 3 to unlock $${limits[2].trading} trading and $${limits[2].withdrawal} withdrawal limits.`
                  : "Complete KYC verification to unlock trading, withdrawals, and access to DeeX&apos;s full features. Your security is our priority."}
              </p>
              <button
                type="button"
                onClick={() => setShowLimitsModal(true)}
                className="mt-2 text-secondary_button dark:text-[#5CC3FF] text-[13px] font-medium underline"
              >
                Learn more about Limits
              </button>
            </div>
            <button
              onClick={handleUpgrade}
              disabled={hasPending}
              className="mt-8 w-full h-[52px] rounded-xl bg-secondary_button text-white font-semibold text-[15px] disabled:bg-gray-400 dark:disabled:bg-gray-600 disabled:cursor-not-allowed"
            >
              Upgrade to level {currentLevel === 3 ? 3 : currentLevel + 1}
            </button>
          </>
        )}
      </div>

      {showLimitsModal && (
        <LimitsInfoModal onClose={() => setShowLimitsModal(false)} />
      )}

      {isPage && openLevel1Flow && (
        <Level1Flow
          setOpenLevel1Flow={setOpenLevel1Flow}
          setOpenKyc={undefined}
          setOpenKycOnboarding={() => {}}
        />
      )}
      {isPage && openLevel2Flow && (
        <Level2Flow
          setOpenLevel2Flow={setOpenLevel2Flow}
          setOpenKyc={undefined}
          setOpenKycOnboarding={() => {}}
        />
      )}
      {isPage && openLevel3Flow && (
        <Level3Flow
          setOpenLevel3Flow={setOpenLevel3Flow}
          setOpenKyc={undefined}
          setOpenKycOnboarding={() => {}}
        />
      )}
    </div>
  );
};

export default KycOnboarding;
export { KYC_VERIFICATION_PATH };
